import { Link, Outlet, useLocation } from "react-router-dom";
import { Leaf, Menu, X } from "lucide-react";
import { useState } from "react";
import { contactInfo, nav, routes, type Locale } from "@renza/shared";
import { quotePath } from "../lib/locale";
import { CTAButton } from "./UI";

function localeFromPath(pathname: string): Locale {
  return pathname.startsWith("/en") ? "en" : "da";
}

export function LanguageSwitcher({ locale }: { locale: Locale }) {
  const da = routes.da.home;
  const en = routes.en.home;
  return (
    <div
      className="flex items-center rounded-full bg-white p-1 text-xs font-semibold ring-1 ring-charcoal/10"
      aria-label="Language switcher"
    >
      <Link
        className={`rounded-full px-3 py-2 ${locale === "da" ? "bg-sage text-pine" : "text-charcoal/60"}`}
        to={da}
      >
        DA
      </Link>
      <Link
        className={`rounded-full px-3 py-2 ${locale === "en" ? "bg-sage text-pine" : "text-charcoal/60"}`}
        to={en}
      >
        EN
      </Link>
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  const items = nav[locale];
  return (
    <header className="sticky top-0 z-40 border-b border-charcoal/10 bg-[#faf9f5]/80 backdrop-blur-2xl">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8"
        aria-label="Main navigation"
      >
        <Link
          to={locale === "da" ? "/" : "/en"}
          className="flex items-center gap-2 text-xl font-extrabold tracking-tight text-pine"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-charcoal/10">
            <Leaf size={18} />
          </span>
          Renza
        </Link>
        <div className="hidden items-center gap-7 lg:flex">
          {items.map(([label, href]) => (
            <Link
              key={href}
              className="text-sm font-medium text-charcoal/60 transition hover:text-pine"
              to={href}
            >
              {label}
            </Link>
          ))}
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher locale={locale} />
          <CTAButton to={quotePath(locale)}>
            {locale === "da" ? "Få tilbud" : "Get quote"}
          </CTAButton>
        </div>
        <button
          className="rounded-full p-2 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Open menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>
      {open && (
        <div className="border-t border-charcoal/10 bg-[#faf9f5] px-4 py-4 lg:hidden">
          <div className="grid gap-3">
            {items.map(([label, href]) => (
              <Link
                key={href}
                onClick={() => setOpen(false)}
                className="rounded-2xl bg-white px-4 py-3 font-medium"
                to={href}
              >
                {label}
              </Link>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <LanguageSwitcher locale={locale} />
            <CTAButton to={quotePath(locale)}>
              {locale === "da" ? "Få tilbud" : "Get quote"}
            </CTAButton>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  return (
    <footer className="border-t border-charcoal/10 bg-white/80">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-4 lg:px-8">
        <div className="md:col-span-2">
          <div className="text-2xl font-extrabold text-pine">Renza</div>
          <p className="mt-3 max-w-md text-charcoal/65">
            Clean spaces. Clear standards.{" "}
            {locale === "da"
              ? "Professionel, miljøvenlig rengøring i København og nærområder."
              : "Professional, eco-friendly cleaning in Copenhagen and nearby areas."}
          </p>
        </div>
        <div>
          <h3 className="font-semibold">Contact</h3>
          <p className="mt-3 text-sm text-charcoal/65">
            {contactInfo.phone}
            <br />
            {contactInfo.email}
          </p>
        </div>
        <div>
          <h3 className="font-semibold">Legal</h3>
          <div className="mt-3 grid gap-2 text-sm">
            <Link
              to={locale === "da" ? "/privatlivspolitik" : "/en/privacy-policy"}
            >
              {locale === "da" ? "Privatlivspolitik" : "Privacy policy"}
            </Link>
            <Link to={locale === "da" ? "/cookiepolitik" : "/en/cookie-policy"}>
              {locale === "da" ? "Cookiepolitik" : "Cookie policy"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Layout() {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Link
        to={quotePath(locale)}
        className="fixed bottom-4 left-4 right-4 z-50 rounded-full bg-pine px-6 py-4 text-center text-sm font-bold text-white shadow-2xl transition active:scale-[0.98] md:hidden"
      >
        {locale === "da" ? "Få tilbud" : "Get quote"}
      </Link>
      <Footer />
    </>
  );
}
